import { createContext, useCallback, useContext, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import useDashboardState from '../hooks/useDashboardState';
import { CropColorFragment } from '../queries';

const colorsQuery = gql`
  ${CropColorFragment}
  query {
    colors {
      ...CropColorFields
    }
  }
`;

const initState = {
  dashboards: {},
  cropColorOptions: [],
  getColorForCropName: () => {},
  resetState: () => {},
};

export const MultiProjectDashboardContext = createContext(initState);

export const MultiProjectDashboardContextProvider = ({ children }) => {
  const { resetDashboards, ...dashboardState } = useDashboardState();
  const { activeDashboard } = dashboardState;
  const cropColorMap = activeDashboard?.cropColorMap;

  const { data: colorOptionsData } = useQuery(colorsQuery);
  const colors = colorOptionsData?.colors;

  // use the cropName and cropColorMap to get the color id
  // then use the color id to get the color object from the colors array
  const getColorForCropName = useCallback(
    (cropName) => {
      const colorId = cropColorMap?.[cropName] || {};
      return colors?.find((c) => c.id === colorId);
    },
    [cropColorMap, colors],
  );

  const value = useMemo(
    () => ({
      dashboards: { ...dashboardState },
      cropColorOptions: colors || [],
      getColorForCropName,
      resetState: resetDashboards,
    }),
    [colors, dashboardState, getColorForCropName, resetDashboards],
  );

  return (
    <MultiProjectDashboardContext.Provider value={value}>
      {children}
    </MultiProjectDashboardContext.Provider>
  );
};

export const useMultiProjectDashboardContext = () =>
  useContext(MultiProjectDashboardContext);
